<template>
  <div :id="`${source}_upload_recording_component`">
    <div
      class="upload_container"
      :style="containerStyle"
      @drop.prevent="handleDrop"
      @dragover.prevent
      @click="triggerFileInput"
    >
      <input
        type="file"
        ref="fileInput"
        :accept="acceptedFileTypes"
        style="display: none"
        @change="handleFileChange"
      />

      <div class="flex flex-row gap-1 text-sm items-center text_color">
        <ThemeImage
          class="size-4 mb-[2px]"
          :icon="imageNames.UPLOAD_GREY"
          alt="upload_icon"
        />
        <span class="font-semibold">Drag & drop</span>
        or
        <span class="font-semibold">Click to upload</span>
      </div>

      <span class="text-xs secondary_color">{{ description }}</span>
    </div>
  </div>
</template>

<script setup>

import { computed, ref } from "vue";
import { message } from "ant-design-vue";
import { maxFileSizes } from "@/src/constants/customComponentConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  type: { type: String, required: true },
  height: { type: String, default: "96px" },
  description: { type: String, required: true },
  modelValue: { type: [File, null], default: null },
});

const emit = defineEmits(["update:modelValue"]);

const fileInput = ref(null);

const FILE_TYPES = {
  audio: {
    extensions: ".mp3,.wav,.aac,.ogg,.mpeg",
    mimeTypes: [
      "audio/mpeg",
      "audio/wav",
      "audio/aac",
      "audio/ogg",
      "audio/x-m4a",
      "audio/mp3",
      "audio/mpeg3",
      "audio/x-mpeg-3",
    ],
    regex: /\.(mp3|wav|aac|ogg|mpeg)$/i,
  },
  image: {
    extensions: ".jpg,.jpeg,.png,.gif,.webp",
    mimeTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    regex: /\.(jpg|jpeg|png|gif|webp)$/i,
  },
  video: {
    extensions: ".mp4,.webm,.mov",
    mimeTypes: ["video/mp4", "video/webm", "video/quicktime"],
    regex: /\.(mp4|webm|mov)$/i,
  },
  document: {
    extensions: ".pdf",
    mimeTypes: ["application/pdf"],
    regex: /\.pdf$/i,
  },
  esign_doc: {
    extensions: ".pdf",
    mimeTypes: ["application/pdf"],
    regex: /\.pdf$/i,
  },
};

const acceptedFileTypes = computed(() => {
  if (!FILE_TYPES[props.type]) {
    throw new Error(`Invalid file type for ${props.type}`);
  }
  return FILE_TYPES[props.type].extensions;
});

const containerStyle = computed(() => ({
  height: props.height,
}));

const validateFileType = (file) => {
  const allowedTypes = FILE_TYPES[props.type]?.mimeTypes;
  const fileExtension = file.name.toLowerCase();
  const regex = FILE_TYPES[props.type]?.regex;

  return (
    (allowedTypes && allowedTypes.includes(file.type)) ||
    (regex && regex.test(fileExtension))
  );
};

const validateFileSize = (file) => {
  const maxSize = maxFileSizes[props.type];
  return file.size <= maxSize;
};

const processFile = (file) => {
  if (!validateFileType(file)) {
    message.error(`Please upload a valid ${props.type} file`);
    return;
  }

  if (!validateFileSize(file)) {
    const maxSizeMB = maxFileSizes[props.type] / (1024 * 1024);
    message.error(`File size exceeds the limit of ${maxSizeMB}MB`);
    return;
  }
  emit("update:modelValue", file);
};

const triggerFileInput = () => fileInput.value?.click();

const handleFileChange = (event) => {
  try {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  } catch (error) {
    console.error("Failed to upload file: ", error);
  } finally {
    event.target.value = "";
  }
};

const handleDrop = (event) => {
  try {
    if (event.dataTransfer.files.length > 1) {
      message.warning("Please drop only one file");
      return;
    }

    const file = event.dataTransfer.files?.[0];
    if (file) processFile(file);
  } catch (error) {
    console.error("Failed to handle drop file: ", error);
  }
};
</script>

<style scoped>
.upload_container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px dashed var(--white-opacity-20);
  background: transparent;
  cursor: pointer;
}
</style>
